import { useEffect, useState } from "react";
import { TProcessStates } from "../../../lib/types/general";
import { LabSignUpFormData, TApiLab, TEvents } from "../../../lib/types/labs";
import dayjs from "dayjs";
import { useLabs } from "../../../services/laboratories";
import { Spinner } from "../../common/spinner";
import { DEVICES_SIZES_IN_PX } from "../../../lib/common/constants";
import { LifestyleOverlayCard } from "./lifestyle-overlay-card";

type TEventsProps = {
    editable: boolean;
    isSomePartBeingEdited: boolean;
    userInfo: TApiLab;
    workingState: TProcessStates;
    setUserInfo: React.Dispatch<React.SetStateAction<TApiLab>>;
    setIsSomePartBeingEdited: (value: React.SetStateAction<boolean>) => void;
    updateLabAndLocalStorage: (data: LabSignUpFormData, name?: string) => Promise<void>;
};

export const Events = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    workingState,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
}: TEventsProps) => {
    const [eventsPerPage, setEventsPerPage] = useState(5);
    const totalEvents = userInfo.other.aboutEvents || [];
    const totalItems = Math.ceil(totalEvents.length / eventsPerPage);
    const [visibleEvents, setVisibleEvents] = useState(5);

    const { getEvents } = useLabs();
    const [isExpandedEvents, setIsExpandedEvents] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState<TEvents[]>([]);
    const [currWorkingStateEvents, setCurrWorkingStateEvents] = useState<TProcessStates>("done");
    const fetchEventsData = async () => {
        const zip = userInfo.other?.zip || "";
        if (!zip || zip === "") {
            return;
        }

        setCurrWorkingStateEvents("working");
        try {
            const response = await getEvents(zip);
            const events = response.data?._embedded?.events || [];
            const userInfoSelectedEventsMap = userInfo.other?.aboutEvents
                ? userInfo.other.aboutEvents.reduce(
                      (prev, curr) => {
                          return {
                              ...prev,
                              [curr.id]: curr,
                          };
                      },
                      {} as Record<string, any>,
                  )
                : {};

            events.forEach((r) => {
                r.isSelected = userInfoSelectedEventsMap[r.id] !== undefined ? true : false;
            });

            setSelectedEvents(events);
        } catch (error) {
            console.error("fetchEventsData - Error fetching data:", error);
        } finally {
            setCurrWorkingStateEvents("idle");
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= DEVICES_SIZES_IN_PX.medium) {
                setEventsPerPage(3);
            } else {
                setEventsPerPage(5);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const renderCarouselItems = () => {
        const carouselItems = [];

        for (let i = 0; i < totalItems; i++) {
            const startIdx = i * eventsPerPage;
            const endIdx = startIdx + eventsPerPage;
            const currentEvents = totalEvents.slice(startIdx, endIdx);

            const cardGroup = currentEvents.map((event) => (
                <div className="col" key={event.id}>
                    <LifestyleOverlayCard
                        className="min-height-cards-lifestyle w-auto"
                        imageSrc={event.images && event.images.length > 0 ? event.images[1].url : ""}
                        imageAlt="event"
                        href={event.url}
                        title={event.name}
                        line1={
                            <>
                                <i className="fa-solid fa-calendar-day text-primary me-2"></i>{" "}
                                {dayjs(event.dates?.start?.localDate).utcOffset(0).format("MMMM D, YYYY")}
                            </>
                        }
                        line2={
                            <>
                                <i className="fa-solid fa-layer-group me-2"></i>
                                {event.classifications?.[0]?.segment?.name || "Unknown Segment"}
                            </>
                        }
                        line3={
                            <>
                                <i className="fa-solid fa-tags me-2"></i>
                                {event.classifications?.[0]?.genre?.name || "Unknown Genre"}
                            </>
                        }
                    />
                </div>
            ));

            //hack to make all the card equally width when there is less than what the page allows
            if (cardGroup.length < eventsPerPage) {
                const remainingCards = eventsPerPage - cardGroup.length;
                for (let j = 0; j < remainingCards; j++) {
                    const DummyEmptyCard = <div className="col" key={`event-empty-${i}-${j}`} aria-hidden="true"></div>;
                    cardGroup.push(DummyEmptyCard);
                }
            }
            carouselItems.push(
                <div className={`carousel-item ${i === 0 ? "active" : ""}`} key={i}>
                    <div className={"row g-3 row-cols-1 row-cols-md-" + eventsPerPage}>{cardGroup}</div>
                </div>,
            );
        }

        return carouselItems;
    };

    const openEventsOptions = () => {
        setIsExpandedEvents(!isExpandedEvents);
        fetchEventsData();
        setIsSomePartBeingEdited(true);
    };
    const handleCheckboxChange = (e: any, events: TEvents) => {
        e.stopPropagation();
        setSelectedEvents((prev) => {
            const idx = prev.findIndex((event) => event.id === events.id);
            prev[idx].isSelected = !prev[idx].isSelected;
            return prev;
        });
    };

    const saveEvents = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        setCurrWorkingStateEvents("working");
        const selectedInfo = selectedEvents.filter((event) => event.isSelected);
        const newEvents = {
            ...userInfo.other,
            aboutEvents: selectedInfo,
        };

        await updateLabAndLocalStorage(
            {
                password: "",
                ...newEvents,
            },
            "",
        );

        setUserInfo({
            ...userInfo,
            other: {
                ...userInfo.other,
                aboutEvents: selectedInfo,
            },
        });

        setIsExpandedEvents(false);
        setIsSomePartBeingEdited(false);
        setCurrWorkingStateEvents("done");
    };

    const cancelHandler = async () => {
        setIsExpandedEvents(false);
        setIsSomePartBeingEdited(false);
    };
    return (
        <>
            {!editable && userInfo.other.aboutEvents && (
                <div className="container mb-4 py-3 lifestyle-panel">
                    <div className="row">
                        <h6 className="mb-3 fw-bold text-primary">UPCOMING EVENTS IN THE AREA</h6>
                        <div className="d-none d-md-block">
                            {/* non editable desktop carrousel - techs view*/}
                            <div className="min-height-cards-lifestyle w-100">
                                {workingState === "working" && <p className="p-3">Looking for events in this area...</p>}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutEvents && userInfo.other.aboutEvents.length > 0 && (
                                    <div id="eventCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="false">
                                        <div className="carousel-inner">{renderCarouselItems()}</div>
                                        {userInfo.other.aboutEvents.length > 5 && (
                                            <div className="">
                                                <a className="carousel-control-prev" href="#eventCarousel" role="button" data-bs-slide="prev">
                                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Previous</span>
                                                </a>
                                                <a className="carousel-control-next" href="#eventCarousel" role="button" data-bs-slide="next">
                                                    <span className="carousel-control-next-icon text-dark" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Next</span>
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutEvents?.length === 0 && <p>No events found.</p>}
                            </div>
                        </div>
                        {/* non editable mobile - this is not a carrousel, its a stack of cards - techs view*/}
                        <div className="d-block d-md-none">
                            {workingState === "working" && <p>Looking for events in this area...</p>}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutEvents && userInfo.other.aboutEvents.length > 0 && (
                                <>
                                    <div>
                                        {userInfo.other.aboutEvents.slice(0, visibleEvents).map((event) => (
                                            <div key={event.id} className="my-2">
                                                <LifestyleOverlayCard
                                                    className="mb-3"
                                                    imageSrc={event.images && event.images.length > 0 ? event.images[1].url : ""}
                                                    imageAlt="event"
                                                    href={event.url}
                                                    title={event.name}
                                                    line1={
                                                        <>
                                                            <i className="fa-solid fa-calendar-day text-primary"></i>{" "}
                                                            {dayjs(event.dates?.start?.localDate).utcOffset(0).format("MMMM D, YYYY")}
                                                        </>
                                                    }
                                                    line2={
                                                        <>
                                                            <i className="fa-solid fa-layer-group"></i>
                                                            {event.classifications?.[0]?.segment?.name || "Unknown Segment"}
                                                        </>
                                                    }
                                                    line3={
                                                        <>
                                                            <i className="fa-solid fa-tags"></i>
                                                            {event.classifications?.[0]?.genre?.name || "Unknown Genre"}
                                                        </>
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {visibleEvents < userInfo.other.aboutEvents.length && (
                                        <div className="text-center">
                                            <button className="btn btn-link fw-semibold" onClick={() => setVisibleEvents((prev) => prev + 5)}>
                                                View More
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutEvents?.length === 0 && <p>No events found.</p>}
                        </div>
                    </div>
                </div>
            )}
            {editable && (
                <div className="container mb-4 py-3 lifestyle-panel">
                    <div className="row">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-bold text-primary">UPCOMING EVENTS IN THE AREA</h6>
                            {!isSomePartBeingEdited && (
                                <div
                                    className="fileTooltip rightEdit"
                                    data-tooltip=" Edit "
                                    role="button"
                                    onClick={openEventsOptions}
                                    aria-expanded="false"
                                    aria-controls="collapseRestaurant"
                                >
                                    <i data-bs-testid="edit-icon" className="fa-solid fa-pen-to-square fa-xs p-2 text-center shadow editIcon"></i>
                                </div>
                            )}
                        </div>
                        {isExpandedEvents && editable && (
                            <div>
                                <div className="card card-body mb-3">
                                    <p>
                                        <strong>Select the events you would like to display.</strong>
                                    </p>
                                    <div>
                                        {currWorkingStateEvents === "working" && (
                                            <div className="text-center">
                                                <Spinner spinnerColor="text-primary" />
                                            </div>
                                        )}
                                        {currWorkingStateEvents === "idle" && selectedEvents.length > 0 && (
                                            <div className="row">
                                                {selectedEvents.map((event) => (
                                                    <div className="col-md-6 col-lg-4 mb-3" key={event.id}>
                                                        <div className="input-group h-100">
                                                            <label className="w-100" htmlFor={`rChk_${event.id}`}>
                                                                <div className="row w-100 d-flex align-items-center input-group-text h-100 p-2">
                                                                    <div className="col-2 p-0">
                                                                        <input
                                                                            type="checkbox"
                                                                            defaultChecked={event.isSelected}
                                                                            id={`rChk_${event.id}`}
                                                                            className="form-check-input"
                                                                            onChange={(e) => handleCheckboxChange(e, event)}
                                                                        />
                                                                    </div>
                                                                    <div className="col-4 p-0">
                                                                        <img
                                                                            src={event.images && event.images.length > 0 ? event.images[1].url : ""}
                                                                            className="rounded label-img"
                                                                            alt="events"
                                                                        />
                                                                    </div>
                                                                    <div className="col-6 text-wrap">
                                                                        <p className="mb-1">
                                                                            {" "}
                                                                            <small>
                                                                                <strong>{event.name}</strong>
                                                                            </small>
                                                                        </p>
                                                                        <p className="mb-0">
                                                                            <small>
                                                                                {dayjs(event.dates?.start?.localDate).utcOffset(0).format("MM/DD/YYYY")}
                                                                            </small>
                                                                        </p>
                                                                        <p>
                                                                            <small>{event.classifications?.[0]?.segment?.name}</small>
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {currWorkingStateEvents === "idle" && selectedEvents.length === 0 && <p>No events found in this area.</p>}
                                    </div>
                                    <div className="d-grid gap-3 d-md-block">
                                        <button className="btn btn-primary" onClick={saveEvents}>
                                            Save
                                        </button>
                                        <button className="btn btn-danger ms-md-3" onClick={cancelHandler}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* editable desktop carrousel - labs view*/}
                        <div className="d-none d-md-block">
                            <div className="min-height-cards-lifestyle w-100">
                                {workingState === "working" && <p className="p-3">Looking for events in this area...</p>}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutEvents && userInfo.other.aboutEvents.length > 0 && (
                                    <>
                                        <div id="eventCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="false">
                                            <div className="carousel-inner">{renderCarouselItems()}</div>
                                            {userInfo.other.aboutEvents.length > 5 && (
                                                <div className="">
                                                    <a className="carousel-control-prev" href="#eventCarousel" role="button" data-bs-slide="prev">
                                                        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                        <span className="visually-hidden">Previous</span>
                                                    </a>
                                                    <a className="carousel-control-next" href="#eventCarousel" role="button" data-bs-slide="next">
                                                        <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                        <span className="visually-hidden">Next</span>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutEvents?.length === 0 && <p>No events found.</p>}
                            </div>
                        </div>
                        {/* editable mobile - this is not a carrousel, its a stack of cards - techs view*/}
                        <div className="d-block d-md-none">
                            {workingState === "working" && <p>Looking for events in this area...</p>}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutEvents && userInfo.other.aboutEvents.length > 0 && (
                                <>
                                    <div>
                                        {userInfo.other.aboutEvents.slice(0, visibleEvents).map((event) => (
                                            <div className="my-2" key={event.id}>
                                                <LifestyleOverlayCard
                                                    className="mb-3"
                                                    imageSrc={event.images && event.images.length > 0 ? event.images[1].url : ""}
                                                    imageAlt="event"
                                                    href={event.url}
                                                    title={event.name}
                                                    line1={
                                                        <>
                                                            <i className="fa-solid fa-calendar-day text-primary me-2"></i>{" "}
                                                            {dayjs(event.dates?.start?.localDate).utcOffset(0).format("MMMM D, YYYY")}
                                                        </>
                                                    }
                                                    line2={
                                                        <>
                                                            <i className="fa-solid fa-layer-group me-2"></i>
                                                            {event.classifications?.[0]?.segment?.name || "Unknown Segment"}
                                                        </>
                                                    }
                                                    line3={
                                                        <>
                                                            <i className="fa-solid fa-tags me-2"></i>
                                                            {event.classifications?.[0]?.genre?.name || "Unknown Genre"}
                                                        </>
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {visibleEvents < userInfo.other.aboutEvents.length && (
                                        <div className="text-center">
                                            <button className="btn btn-link fw-semibold" onClick={() => setVisibleEvents((prev) => prev + 5)}>
                                                View More
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutEvents?.length === 0 && <p>No events found.</p>}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
